import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Grid,
  Button,
  Autocomplete,
  TextField,
  Pagination
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SidebarLayout from '../components/SidebarLayout';

interface CommentRecord {
  id: number;
  client_id: string;
  status: number;
  execution_time: string;
  content: string;
  user?: { username: string }; // 新增
}

interface Statistics {
  total: number;
  success_count: number;
  failed_count: number;
}

interface User {
  id: number;
  username: string;
}

const CommentDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [comments, setComments] = useState<CommentRecord[]>([]);
  const [clients, setClients] = useState<{ client_id: string; hostname: string }[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [selectedUser, setSelectedUser] = useState<number | ''>('');
  const [userSearch, setUserSearch] = useState('');
  const [statistics, setStatistics] = useState<Statistics>({
    total: 0,
    success_count: 0,
    failed_count: 0,
  });
  const [status, setStatus] = useState<string>('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [startTime, setStartTime] = useState<string>('');
  const [endTime, setEndTime] = useState<string>('');

  useEffect(() => {
    fetchData();
  }, [selectedClient, selectedUser, user, status, page, limit, startTime, endTime]);

  const fetchData = async () => {
    try {
      const params = {
        clientId: selectedClient || undefined,
        userId: selectedUser || undefined,
        status,
        page,
        limit,
        startTime: startTime || undefined,
        endTime: endTime || undefined
      };
      const commentsResponse = await axios.get('/api/comments', { params });
      setComments(commentsResponse.data.comments);
      setTotal(commentsResponse.data.total || 0);
      const statsResponse = await axios.get('/api/comments/statistics', { params });
      setStatistics(statsResponse.data);
      // 获取客户端列表
      const clientsResponse = await axios.get('/api/clients');
      setClients(clientsResponse.data);
      // 如果是管理员，获取用户列表
      if (user?.role === 'admin') {
        fetchUsers();
      }
    } catch (error) {
      console.error('获取评论数据失败:', error);
    }
  };

  const fetchUsers = async (search = '') => {
    const usersResponse = await axios.get('/api/users', { params: { search } });
    setUsers(usersResponse.data.users);
  };

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchUsers();
    }
  }, [user]);

  return (
    <SidebarLayout>
      <Container maxWidth='lg' sx={{ mt: 4, mb: 4 }}>
        <Grid container spacing={2}>
          {/* 统计卡片 */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography color='textSecondary' gutterBottom>
                  评论总数
                </Typography>
                <Typography variant='h5'>
                  {statistics.total}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography color='textSecondary' gutterBottom>
                  成功次数
                </Typography>
                <Typography variant='h5' color='primary'>
                  {statistics.success_count}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography color='textSecondary' gutterBottom>
                  失败次数
                </Typography>
                <Typography variant='h5' color='error'>
                  {statistics.failed_count}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* 筛选器 */}
          <Grid item xs={12}>
            <Grid container spacing={2} alignItems="center">
              {user?.role === 'admin' && (
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    options={users}
                    getOptionLabel={(option) => option.username}
                    value={users.find(u => u.id === selectedUser) || null}
                    onChange={(_, newValue) => setSelectedUser(newValue ? newValue.id : '')}
                    inputValue={userSearch}
                    onInputChange={(_, newInputValue) => {
                      setUserSearch(newInputValue);
                      fetchUsers(newInputValue);
                    }}
                    renderInput={(params) => <TextField {...params} label="选择用户" variant="outlined" fullWidth />}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    clearOnBlur={false}
                    sx={{ width: '100%' }}
                  />
                </Grid>
              )}
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>状态</InputLabel>
                  <Select
                    value={status}
                    label="状态"
                    onChange={e => {
                      setStatus(e.target.value);
                      setPage(1);
                    }}
                  >
                    <MenuItem value="">全部</MenuItem>
                    <MenuItem value="1">成功</MenuItem>
                    <MenuItem value="0">失败</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="开始时间"
                  type="datetime-local"
                  value={startTime}
                  onChange={e => setStartTime(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="结束时间"
                  type="datetime-local"
                  value={endTime}
                  onChange={e => setEndTime(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Grid>

          {/* 评论记录表格 */}
          <Grid item xs={12}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>时间</TableCell>
                    <TableCell>状态</TableCell>
                    <TableCell>内容</TableCell>
                    <TableCell>操作人</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {comments.map((comment) => (
                    <TableRow key={comment.id}>
                      <TableCell>
                        {new Date(comment.execution_time).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Typography color={comment.status === 1 ? 'success.main' : 'error.main'}>
                          {comment.status === 1 ? '成功' : '失败'}
                        </Typography>
                      </TableCell>
                      <TableCell>{comment.content}</TableCell>
                      <TableCell>{comment.user?.username}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {/* 分页控件 */}
          <Grid item xs={12} sx={{ mt: 2, display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center' }}>
            <Pagination
              count={Math.ceil(total / limit)}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
            />
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              总条数: {total}
            </Typography>
          </Grid>
        </Grid>
      </Container>
    </SidebarLayout>
  );
};

export default CommentDashboard; 